import { useCallback, useMemo, useRef, useState } from 'react';
import { getFilePreviewType, useThumbnails, type Asset } from '@odo/shared-ui';
import { filesApi } from '../../../../services/filesApi';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';

export interface SubmissionFileAccess {
    thumbnailFor: (asset: Asset) => string | null;
    onThumbnailError: (asset: Asset) => void;
    /** Opens an image in the in-app viewer; a no-op for any other file type. */
    openFile: (asset: Asset) => void;
    /** patientFileId of the image whose signed URL is being resolved. */
    openingId: string | null;
    /** Image currently shown in the in-app viewer, if any. */
    viewer: ImageViewerState | null;
    closeViewer: () => void;
}

interface ImageViewerState {
    src: string;
    alt: string;
}

/**
 * Read-only file access for the submission viewer: grid thumbnails through the
 * shared files module, and images opened in the in-app viewer from a freshly
 * signed URL. Nothing here downloads a file.
 */
export function useSubmissionFileAccess(
    patientId: string,
    assets: readonly Asset[]
): SubmissionFileAccess {
    const { showToast } = useToast();
    const [openingId, setOpeningId] = useState<string | null>(null);
    const [viewer, setViewer] = useState<ImageViewerState | null>(null);
    const closeViewer = useCallback(() => setViewer(null), []);

    const assetMap = useMemo(
        () => Object.fromEntries(assets.map((a) => [String(a.assetId), a])),
        [assets]
    );
    const { thumbnailUrls, handleThumbnailError } = useThumbnails({
        patientId,
        assets: assetMap,
        layoutMode: 'grid',
        api: filesApi
    });

    const thumbnailFor = useCallback(
        (asset: Asset): string | null => thumbnailUrls[String(asset.assetId)] ?? null,
        [thumbnailUrls]
    );

    const onThumbnailError = useCallback(
        (asset: Asset) => {
            void handleThumbnailError(String(asset.assetId));
        },
        [handleThumbnailError]
    );

    // Only the latest click may update state: a slower earlier request must not
    // clear the newer "Opening..." or replace the newer image in the viewer.
    const latestRequestRef = useRef(0);

    const openFile = useCallback(
        (asset: Asset) => {
            const patientFileId = asset.patientFileId;
            if (!patientFileId || getFilePreviewType(asset.name) !== 'image') return;
            latestRequestRef.current += 1;
            const requestId = latestRequestRef.current;
            const isLatest = (): boolean => requestId === latestRequestRef.current;
            setOpeningId(patientFileId);
            filesApi
                .getDownloadUrl(patientId, patientFileId)
                .then((url) => {
                    if (isLatest()) setViewer({ src: url, alt: asset.name });
                })
                .catch((error: unknown) => {
                    if (isLatest()) {
                        showToast(apiErrorMessage(error, 'Failed to open the image'), 'error');
                    }
                })
                .finally(() => {
                    if (isLatest()) setOpeningId(null);
                });
        },
        [patientId, showToast]
    );

    return { thumbnailFor, onThumbnailError, openFile, openingId, viewer, closeViewer };
}
